import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { IUser, IUserPage } from 'src/app/models/user.model';
import { UserService } from 'src/app/services/user.service';
import { SearchService } from '../../services/search.service';
import { 
  faAngleDoubleLeft, 
  faAngleDoubleRight, 
  faAngleLeft, 
  faAngleRight, 
  faCircle,
  faSort, 
  faSortDown, 
  faSortUp, 
  faTimes 
} from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-users-v2',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.sass']
})
export class UsersV2Component implements OnInit, OnDestroy {
  loading = false;
  pageSize = 10;
  currentPage = 0;
  totalPages = 1;
  totalElements = 0;
  sortBy = 'ASC';
  orderBy = 'name';
  searchTerm: string | undefined = undefined;
  users: IUser[] = [];
  private searchSubscription?: Subscription;

  // Delete modal
  showConfirmDeletePopup = false;
  userToDelete = '';

  // Expor Math para o template
  Math = Math;

  // Mapa de permissões
  permissionsMap: { [key: string]: string } = {
    'ROOT': 'Root',
    'ADMIN': 'Administrador',
    'ATTENDANT': 'Atendente',
  };

  pageSizeOptions = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
    { value: 1000, label: 'Todos' }
  ];

  // Icons
  faSort = faSort;
  faSortUp = faSortUp;
  faSortDown = faSortDown;
  faCircle = faCircle;
  faAngleDoubleLeft = faAngleDoubleLeft;
  faAngleLeft = faAngleLeft;
  faAngleRight = faAngleRight;
  faAngleDoubleRight = faAngleDoubleRight;
  faTimes = faTimes;

  constructor(
    private userService: UserService,
    private router: Router,
    private searchService: SearchService
  ) {}

  ngOnInit(): void {
    this.loading = true;
    
    // Limpa o termo de busca ao entrar na página
    this.searchService.setSearchTerm('');
    
    // Carrega a lista inicial
    this.getUsers(0, this.pageSize);

    // Escuta mudanças no termo de pesquisa do header
    this.searchSubscription = this.searchService.searchTerm$.subscribe((term: string) => {
      this.searchTerm = term || undefined;
      this.getUsers(0, this.pageSize);
    });
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  toggleSort(column: string): void {
    if (this.orderBy === column) {
      this.sortBy = this.sortBy === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.orderBy = column;
      this.sortBy = 'ASC';
    }
    this.getUsers(0, this.pageSize);
  }

  getUsers(pageNumber: number, pageSize: number): void {
    this.loading = true;
    this.currentPage = pageNumber;
    this.userService.listUsers(pageNumber, pageSize, this.sortBy, this.orderBy, this.searchTerm || '')
      .subscribe({
        next: (usersPaged) => {
          const page = usersPaged as IUserPage;
          this.users = page.data;
          if (page.page_info) {
            this.currentPage = page.page_info.current_page;
            this.totalPages = page.page_info.total_pages;
            this.totalElements = page.page_info.total_elements;
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('Erro ao buscar usuários:', error);
          this.loading = false;
        }
      });
  }

  goToFirstPage(): void {
    if (this.currentPage > 0) {
      this.getUsers(0, this.pageSize);
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 0) {
      this.getUsers(this.currentPage - 1, this.pageSize);
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.getUsers(this.currentPage + 1, this.pageSize);
    }
  }

  goToLastPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.getUsers(this.totalPages - 1, this.pageSize);
    }
  }

  onPageSizeChange(newSize: number): void {
    this.pageSize = newSize;
    this.getUsers(0, this.pageSize);
  }

  generatePagination(current: number, total: number): (string | number)[] {
    const range = (start: number, end: number): number[] => {
      return Array.from({ length: (end - start) }, (v, k) => k + start);
    };
    let pages: (string | number)[] = [1];
    let left = current - 1;
    let right = current + 1;
    if (left < 2) right += 2 - left;
    if (right > total - 1) left -= right - (total - 1);
    left = Math.max(2, left);
    right = Math.min(total - 1, right);
    if (left > 2) pages.push('...');
    pages = pages.concat(range(left, right + 1));
    if (right < total - 1) pages.push('...');
    if (total > 1 && total !== pages[pages.length - 1]) pages.push(total);
    return pages;
  }

  navigateTo(page: number | string): void {
    if (page !== '...') {
      this.getUsers(+page - 1, this.pageSize);
    }
  }

  edit(id?: string): void {
    this.router.navigateByUrl('/main/v2', { skipLocationChange: true }).then(() => {
      this.router.navigate(['main', 'v2', 'users', id, 'edit']);
    });
  }

  showDeleteModal(id?: string): void {
    this.showConfirmDeletePopup = true;
    this.userToDelete = id || '';
  }

  closeDeleteModal(): void {
    this.showConfirmDeletePopup = false;
    this.userToDelete = '';
  }

  onDelete(): void {
    if (!this.userToDelete) return;

    this.loading = true;
    this.showConfirmDeletePopup = false;

    this.userService.deleteUser(this.userToDelete).subscribe({
      next: () => {
        this.getUsers(this.currentPage, this.pageSize);
        this.userToDelete = '';
      },
      error: (error) => {
        console.error('Erro ao deletar usuário:', error);
        this.loading = false;
        this.userToDelete = '';
      }
    });
  }

  getPermissionLabel(permission?: string): string {
    return this.permissionsMap[permission || ''] || permission || 'Não definido';
  }
}
